import { forwardRef, useRef, useCallback, useEffect, useId, useMemo } from "react";
import { useForkRef } from "@mui/material/utils";
import { useVirtualizer } from "@tanstack/react-virtual";
import { LISTBOX_PADDING_PX, OVERSCAN } from "../constants";
import useListboxRows from "../hooks/useListboxRows";
import useStickyGroupHeaders from "../hooks/useStickyGroupHeaders";
import type { GroupRow, VirtualListboxConfig, VisibleSegment } from "../types";
import ListboxGroup from "./ListboxGroup";
import ListboxGroupHeader from "./ListboxGroupHeader";
import ListboxOption from "./ListboxOption";
import ListboxSpacer from "./ListboxSpacer";
import List, { type ListProps } from "components/primitives/List";
import { styled } from "@mui/material";

const ListRoot = styled(List)(() => ({
  boxSizing: "border-box",
  overflow: "auto",
  position: "relative",
  margin: 0,
  listStyle: "none",
}));

interface VirtualListboxProps extends ListProps {
  config: VirtualListboxConfig<unknown>;
}

/**
 * The Autocomplete's listbox slot: places one row per option and mounts only
 * the rows in view.
 */
const VirtualListbox = forwardRef<HTMLUListElement, VirtualListboxProps>((props, fRef) => {
  const { config, children, ...rest } = props;

  const { rows, optionRowIndex, groupRowIndexes } = useListboxRows<unknown>(children);

  config.optionRowIndexRef.current = optionRowIndex;

  const scrollRef = useRef<HTMLUListElement | null>(null);
  const setScrollRef = useForkRef(scrollRef, fRef);

  const getScrollElement = useCallback(() => scrollRef.current, []);

  const { rowHeight, estimateItemHeight } = config;
  const estimateSize = useCallback(() => rowHeight ?? estimateItemHeight, [rowHeight, estimateItemHeight]);

  // Measurements are cached per key, so they must follow the row, not its
  // position — otherwise filtering leaves rows sized for whatever used to
  // sit at that index.
  const getItemKey = useCallback((index: number) => rows[index].key, [rows]);

  const { sticky, rangeExtractor } = useStickyGroupHeaders(groupRowIndexes);

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement,
    estimateSize,
    overscan: OVERSCAN,
    getItemKey,
    rangeExtractor,
    // Rows are placed absolutely, so they would sit inside the listbox's
    // padding that Autocomplete's in-flow options are pushed below.
    paddingStart: LISTBOX_PADDING_PX,
    // Keeps a keyboard-highlighted row from landing underneath the pinned header.
    scrollPaddingStart: sticky ? (rowHeight ?? estimateItemHeight) : 0,
  });

  useEffect(() => {
    config.virtualizerRef.current = virtualizer;
    return () => {
      if (config.virtualizerRef.current === virtualizer) {
        config.virtualizerRef.current = null;
      }
    };
  }, [config, virtualizer]);

  const lastResetKey = useRef(config.resetScrollKeyRef.current);
  useEffect(() => {
    const resetKey = config.resetScrollKeyRef.current;
    if (lastResetKey.current !== resetKey) {
      lastResetKey.current = resetKey;
      virtualizer.scrollToOffset(0);
    }
  });

  const virtualItems = virtualizer.getVirtualItems();
  const { measurementsCache } = virtualizer;

  const idBase = useId();
  const groupLabelId = (groupRowIndex: number) => `${idBase}group-${groupRowIndex}`;

  // Rows of a fixed height never need measuring, so they carry no observer.
  const measureElement = rowHeight ? undefined : virtualizer.measureElement;
  const renderContent = config.renderContentRef.current;
  const renderGroupLabel = config.renderGroupLabelRef.current;

  // Ungrouped options stay direct children of the listbox; grouped ones are
  // gathered under their group, with its header when that is in range.
  const segments = useMemo(() => {
    const visible: VisibleSegment[] = [];

    virtualItems.forEach((item) => {
      const row = rows[item.index];
      if (row.kind === "group") {
        visible.push({ kind: "group", rowIndex: item.index, row, header: item, options: [] });
        return;
      }

      if (row.groupRowIndex < 0) {
        visible.push({ kind: "option", item, row });
        return;
      }

      const open = visible[visible.length - 1];
      if (open?.kind === "group" && open.rowIndex === row.groupRowIndex) {
        open.options.push({ item, row });
        return;
      }

      visible.push({
        kind: "group",
        rowIndex: row.groupRowIndex,
        row: rows[row.groupRowIndex] as GroupRow,
        options: [{ item, row }],
      });
    });

    return visible;
  }, [virtualItems, rows]);

  return (
    <ListRoot ref={setScrollRef} {...rest}>
      <ListboxSpacer height={virtualizer.getTotalSize() - LISTBOX_PADDING_PX} />
      {segments.map((segment) => {
        if (segment.kind === "option") {
          return (
            <ListboxOption
              key={segment.row.key}
              row={segment.row}
              index={segment.item.index}
              start={segment.item.start}
              measureElement={measureElement}
              renderContent={renderContent}
            />
          );
        }

        const groupStart = measurementsCache[segment.rowIndex]?.start ?? 0;
        const groupEnd = measurementsCache[segment.row.lastRowIndex]?.end ?? groupStart;

        return (
          <ListboxGroup
            key={segment.row.key}
            labelledBy={groupLabelId(segment.rowIndex)}
            label={segment.row.label}
            start={groupStart}
            size={groupEnd - groupStart}
            header={
              segment.header ? (
                <ListboxGroupHeader
                  id={groupLabelId(segment.rowIndex)}
                  label={segment.row.label}
                  index={segment.rowIndex}
                  measureElement={measureElement}
                  renderLabel={renderGroupLabel}
                />
              ) : null
            }
          >
            {segment.options.map(({ item, row }) => (
              <ListboxOption
                key={row.key}
                row={row}
                index={item.index}
                start={item.start - groupStart}
                measureElement={measureElement}
                renderContent={renderContent}
              />
            ))}
          </ListboxGroup>
        );
      })}
    </ListRoot>
  );
});

export default VirtualListbox;
